import { ExpenseData, NoteStatus } from '../types/type';

interface TemplateOptions {
  t: (key: string) => string;
  formatDate: (date: string) => string;
  formatDateOnly: (date: string) => string;
}

export const generateExpenseHTML = (
  expenseData: ExpenseData,
  options: TemplateOptions,
): string => {
  const { t, formatDate, formatDateOnly } = options;
  const { note, clientName } = expenseData;

  // Reference of the expense note
  const noteReference = note.noteReference;

  const successApprovals = note.userslistApproval
    ?.filter((x) => x.actionType === 'Success')
    .map(
      (approval) => `
      <div class="approval-item">
        <table class="approval-layout">
          <tr>
            <td class="approver-info">
              <div class="approver-name">${approval.fullName}</div>
              <div class="approver-department">${approval.departement}</div>
            </td>
            <td class="approval-details">
              <div>${t(approval.status)}</div>
              ${
                approval.status === 'Approved'
                  ? `<div style="padding-top: 5px;">${formatDate(approval.actionDate)}</div>`
                  : '<div style="padding-top: 5px;">-</div>'
              }
            </td>
          </tr>
        </table>
      </div>
    `,
    )
    .join('');

  // Generate approval section for Refund actions
  const refundApprovals = note.userslistApproval
    ?.filter((x) => x.actionType === 'Refund')
    .map(
      (approval) => `
      <div class="approval-item">
        <table class="approval-layout">
          <tr>
            <td class="approver-info">
              <div class="approver-name">${approval.fullName}</div>
              <div class="approver-department">${approval.departement}</div>
            </td>
            <td class="approval-details">
              <div>
                <span class="status-badge ${approval.status === 'Pending' ? 'status-pending' : 'status-approved'}">
                  ${approval.status === 'Pending' ? t(approval.status) : t('Note.Remborse')}
                </span>
              </div>
              ${
                approval.status === 'Approved'
                  ? `<div style="margin-top: 5px;">${formatDate(approval.actionDate)}</div>`
                  : '<div style="margin-top: 5px;">-</div>'
              }
            </td>
          </tr>
        </table>
      </div>
    `,
    )
    .join('');

  const expensesRows = note.allExpenses
    ?.map(
      (expense) => `
      <tr>
        <td>${expense.title}</td>
        <td class="amount">${expense.infoAmount?.toFixed(2)} ${note.currency}</td>
        <td>${formatDateOnly(expense.date)}</td>
      </tr>
    `,
    )
    .join('');

  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>Expense Report</title>
    <style>
        * {
            margin: 0;
            padding: 0;
        }

        body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
            background: white;
            max-width: 210mm;
            margin: 0 auto;
            padding: 20mm;
        }

        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #2C7955;
            padding-bottom: 20px;
        }

        .header-table {
            width: 100%;
            border-collapse: collapse;
        }

        .header-table td {
            vertical-align: top;
        }

        .header-ref,
        .header-spacer {
            width: 20%;
        }

        .header-main {
            width: 60%;
            text-align: center;
        }

        .header-reference {
            display: inline-block;
            padding: 6px 12px;
            background-color: #E0EBE6;
            border: 1px solid #A0C3B2;
            border-radius: 4px;
            font-size: 14px;
            font-weight: bold;
            color: #2C7955;
            letter-spacing: 0.5px;
        }

        .company-logo {
            font-size: 24px;
            font-weight: bold;
            color: #2C7955;
            padding-bottom: 15px;
        }

        .report-title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
            margin: 10px 0;
        }

        .section {
            margin-bottom: 20px;
        }

        .section-title {
            background-color: #2C7955;
            color: white;
            padding: 8px 12px;
            font-weight: bold;
            font-size: 14px;
            line-height: 0.8;
        }

        .info-grid {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 10px;
            padding: 10px;
            padding-top: 5px;
            background: #f8f9fa;
        }

        .info-grid td {
            width: 50%;
            padding: 5px 5px 5px 5px;
            vertical-align: top;
        }

        .info-grid td:last-child {
            padding-right: 0;
        }

        .info-label {
            font-weight: bold;
            color: #666;
            font-size: 11px;
            text-transform: uppercase;
            padding-bottom: 5px;
        }

        .info-value {
            font-size: 13px;
            color: #333;
            border-bottom: 1px solid #eee;
            padding-bottom: 3px;
        }

        .status-badge {
            display: inline-block;
            padding: 4px 8px !important;
            border: 1px solid #ccc !important;
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
        }

        .status-approved {
            background-color: #E0EBE6;
            color: #225e1a;
            border-color: #A0C3B2 !important;
        }

        .status-pending {
            background-color: #fff3cd;
            color: #856404;
            border-color: #ffeaa7;
        }

        .status-reimbursed {
            background-color: #A0C3B2;
            color: #225e1a;
            border-color: #2C7955;
        }

        .approval-item-container {
            background: #f8f9fa;
            padding: 10px;
            padding-top: 5px;
        }

        .approval-item {
            margin-bottom: 5px;
            padding: 5px;
            width: 100%;
        }

        .approval-layout {
            width: 100%;
            border-collapse: collapse;
        }

        .approval-layout td {
            padding: 0;
            vertical-align: top;
        }

        .approver-info {
            width: 70%;
        }

        .approval-details {
            width: 30%;
            text-align: right;
        }

        .approver-name {
            font-weight: 400;
            color: #333;
            margin-bottom: 2px;
            font-size: 0.85em;
            padding-bottom: 5px;
        }

        .approver-department {
            color: #666;
            font-size: 11px;
        }

        .approval-details {
            font-size: 11px;
            color: #666;
        }

        .expenses-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            background: white;
        }

        .expenses-table th {
            background-color: #f8f9fa;
            padding: 10px 8px;
            text-align: left;
            border: 1px solid #dee2e6;
            font-weight: bold;
            color: #333;
            font-size: 11px;
        }

        .expenses-table td {
            padding: 8px;
            border: 1px solid #dee2e6;
            vertical-align: top;
        }

        .amount {
            text-align: right;
            font-weight: bold;
            color: #2C7955;
        }

        .expenses-summary {
            text-align: right;
            margin-top: 15px;
            padding: 10px;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
        }

        .total-amount {
            font-size: 16px;
            font-weight: bold;
            color: #2C7955;
        }

        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            color: #666;
            font-size: 10px;
        }
    </style>
</head>
<body>
    <div class="header">
        <table class="header-table">
            <tr>
                <td class="header-ref">
                    ${
                      noteReference
                        ? `<span class="header-reference">#${noteReference}</span>`
                        : ''
                    }
                </td>
                <td class="header-main">
                    <div class="company-logo">${clientName}</div>
                    <div class="report-title">${t('ExportExpensePdf.Title')}</div>
                </td>
                <td class="header-spacer"></td>
            </tr>
        </table>
    </div>

    <div class="section">
        <div class="section-title">1 - ${t('ExportExpensesPdf.Section1.title')}</div>
        <table class="info-grid">
            <tr>
                <td>
                    <div class="info-label">${t('F.Title')}</div>
                    <div class="info-value">${note.title}</div>
                </td>
                <td>
                    <div class="info-label">${t('status')}</div>
                    <div class="info-value">${t(NoteStatus[note.status])}</div>
                </td>
            </tr>
            <tr>
                <td>
                    <div class="info-label">${t('date')}</div>
                    <div class="info-value">${formatDateOnly(note.executionDate)}</div>
                </td>
                <td>
                    <div class="info-label">${t('Note.Affectedto')}</div>
                    <div class="info-value">${note.userImage?.fullName || ''}</div>
                </td>
            </tr>
            ${
              note.currency
                ? `
            <tr>
                <td>
                    <div class="info-label">${t('Currency')}</div>
                    <div class="info-value">${note.currency}</div>
                </td>
                ${
                  note.description
                    ? `
                <td>
                    <div class="info-label">${t('Expenses.Remark')}</div>
                    <div class="info-value">${note.description}</div>
                </td>
                `
                    : '<td></td>'
                }
            </tr>
            `
                : ''
            }
        </table>
    </div>

    <div class="section">
        <div class="section-title">2 - ${t('ExportExpensesPdf.Section2.title')}</div>
        <div class="approval-item-container">
            ${successApprovals}
            ${refundApprovals}
        </div>
    </div>

    <div class="section">
        <div class="section-title">3 - ${t('ExportExpensesPdf.Section3.title')}</div>
        <table class="expenses-table">
            <thead>
                <tr>
                    <th style="width: 20%;">${t('Category')}</th>
                    <th style="width: 20%;">${t('Expense.Amount')}</th>
                    <th style="width: 20%;">${t('date')}</th>
                </tr>
            </thead>
            <tbody>
                ${expensesRows}
            </tbody>
        </table>

        <div class="expenses-summary">
            <div>${t('ExpenseTotale.Number')}: <strong>${note.allExpenses?.length || 0}</strong></div>
            <div class="total-amount">${t('Expenses.TotalAmount')}: ${note.totalExpenses?.toFixed(2)} ${note.currency}</div>
        </div>
    </div>

    <div class="footer">
        <div style="margin-top: 10px;">
            <div>${t('Expenses.Report.GeneratedDate')}: <span id="generated-date">${formatDate(new Date().toISOString())}</span></div>
        </div>
    </div>
</body>
</html>
  `;
};
